import { useState, useEffect } from "react";
import { queryKeys } from "@/api/queryKeys";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchAuthStatus, fetchLoginUrl, createSession } from "@/api/client";
import { ExternalLink, KeyRound, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { isStateStoreEnabled } from "@/state/store";
import { useKiteAuthenticated } from "@/state/hooks/useAuthStatusState";

export default function AuthSetup() {
  const qc = useQueryClient();
  const [token, setToken] = useState("");
  const [err, setErr] = useState("");
  const [autoProcessing, setAutoProcessing] = useState(false);

  // Phase B.1: prefer state-store; legacy useQuery only when opted out.
  const _storeOn = isStateStoreEnabled();
  const _storeAuthed = useKiteAuthenticated();
  const { data: legacyStatus, isLoading: authLoading, isFetched: authFetched } = useQuery({
    queryKey: queryKeys.authStatus,
    queryFn: fetchAuthStatus,
    enabled: !_storeOn,
  });
  const status = _storeOn
    ? { authenticated: _storeAuthed }
    : legacyStatus;
  const { data: loginData, isLoading: loginLoading } = useQuery({ queryKey: queryKeys.loginUrl, queryFn: fetchLoginUrl });

  const session = useMutation({
    mutationFn: (t: string) => createSession(t),
    onSuccess: () => {
      setErr("");
      setAutoProcessing(false);
      // Clean the URL — remove request_token from query string
      window.history.replaceState({}, document.title, window.location.pathname);
      qc.invalidateQueries({ queryKey: queryKeys.authStatus });
    },
    onError: (e: any) => {
      setErr(e.response?.data?.detail ?? e.message);
      setAutoProcessing(false);
    },
  });

  // Auto-capture request_token from redirect URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestToken = params.get("request_token");
    const loginStatus = params.get("status");
    if (requestToken && loginStatus !== "cancelled") {
      setAutoProcessing(true);
      session.mutate(requestToken);
    }
  }, []);

  if (status?.authenticated) return null;

  // While the first auth-status fetch is in flight, don't render the yellow
  // "Connect to Zerodha" banner — on a hot reload the session is almost
  // always already valid server-side, so flashing the banner for 200-500ms
  // just looks like the system thinks we're logged out. Wait until we've
  // actually heard back from the server before deciding what to show.
  if (authLoading || !authFetched) return null;

  if (autoProcessing) {
    return (
      <div className="bg-yellow-950/20 border border-yellow-500/20 rounded-2xl p-5">
        <div className="flex items-center gap-3 text-yellow-400">
          <Loader2 size={15} className="animate-spin" />
          <span className="font-semibold text-sm">Completing Zerodha login…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-yellow-950/20 border border-yellow-500/20 rounded-2xl p-5">
      <div className="flex items-center gap-2 font-semibold text-yellow-400 mb-3">
        <KeyRound size={15} />
        Connect to Zerodha to start live data & trading
      </div>
      <p className="text-zinc-500 text-xs mb-4">
        Click the button below. After you log in, Zerodha will redirect back here and authenticate automatically.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <a
          href={loginData?.url ?? "#"}
          onClick={!loginData?.url ? (e) => e.preventDefault() : undefined}
          className={`flex items-center justify-center gap-2 text-white font-semibold text-sm px-5 py-2.5 rounded-xl transition whitespace-nowrap ${
            loginData?.url ? "bg-brand hover:bg-indigo-500 cursor-pointer" : "bg-zinc-700 cursor-not-allowed opacity-60"
          }`}
        >
          {loginLoading ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
          {loginLoading ? "Loading…" : "Open Zerodha Login"}
        </a>
        {/* Manual fallback */}
        <input
          className="input flex-1 text-sm"
          placeholder="Or paste request_token manually…"
          value={token}
          onChange={(e) => setToken(e.target.value)}
        />
        <button
          onClick={() => session.mutate(token)}
          disabled={!token || session.isPending}
          className="flex items-center justify-center gap-2 bg-buy hover:bg-green-400 disabled:opacity-40 text-black font-bold text-sm px-5 py-2.5 rounded-xl transition whitespace-nowrap"
        >
          <CheckCircle2 size={14} />
          {session.isPending ? "..." : "Authenticate"}
        </button>
      </div>
      {err && (
        <div className="flex items-center gap-2 text-sell text-xs mt-3">
          <AlertCircle size={12} /> {err}
        </div>
      )}
    </div>
  );
}
